import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyPro, setShowOnlyPro] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(true);
  const adminEmail = "devabyltd@gmail.com";

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const snapshot = await getDocs(collection(db, "deletionFeedback"));
        const feedbackList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFeedback(feedbackList);
        setLoadingFeedback(false);
      } catch (err) {
        console.error("Error fetching feedback", err);
      }
    };

    fetchFeedback();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        const userList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(userList);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching users", err);
      }
    };

    fetchUsers();
  }, []);

  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.email !== adminEmail) {
    return (
      <Container sx={{ mt: 8 }}>
        <Typography variant="h6">Unauthorized</Typography>
        <Typography>
          You must be the SortlyBooks owner to view this page.
        </Typography>
      </Container>
    );
  }
  const uniqueUsersByEmail = new Map();

  users.forEach((user) => {
    if (
      user.email &&
      user.email !== adminEmail &&
      user.email !== auth.currentUser?.email &&
      (!showOnlyPro || user.isPro)
    ) {
      uniqueUsersByEmail.set(user.email, user); // overrides duplicates
    }
  });

  const filteredUsers = Array.from(uniqueUsersByEmail.values());

  const proUsers = filteredUsers.filter((user) => user.isPro);
  const expectedMonthlyRevenue = (proUsers.length * 14.99).toFixed(2);

  const newTodayCount = filteredUsers.filter(
    (u) => u.createdAt?.toDate?.().toDateString() === new Date().toDateString(),
  ).length;

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Admin Dashboard
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  TOTAL USERS
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {filteredUsers.length}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  NEW TODAY
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {newTodayCount}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  EST. MONTHLY REVENUE 💸
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: "green" }}
                >
                  ${expectedMonthlyRevenue}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyPro}
                      onChange={() => setShowOnlyPro(!showOnlyPro)}
                      color="primary"
                    />
                  }
                  label="Show only Pro users"
                />
              </Paper>
            </Grid>
          </Grid>

          <Paper elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Email</b>
                  </TableCell>
                  <TableCell>
                    <b>Signup Date</b>
                  </TableCell>
                  <TableCell>
                    <b>Trial Ends</b>
                  </TableCell>
                  <TableCell>
                    <b>Status</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  const created =
                    user.createdAt?.toDate?.().toLocaleDateString() || "—";

                  const trialEnds = user.trialStartedAt?.toDate
                    ? new Date(
                        user.trialStartedAt.toDate().getTime() +
                          14 * 24 * 60 * 60 * 1000,
                      ).toLocaleDateString()
                    : "—";

                  const isPro = user.isPro ? (
                    <Chip label="Pro" color="success" size="small" />
                  ) : (
                    <Chip label="Free" color="warning" size="small" />
                  );

                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <RouterLink
                          to={`/admin/user/${user.id}`}
                          style={{
                            textDecoration: "none",
                            color: "#1976d2",
                            fontWeight: 500,
                          }}
                        >
                          {user.email || "—"}
                        </RouterLink>
                      </TableCell>
                      <TableCell>{created}</TableCell>
                      <TableCell>{trialEnds}</TableCell>
                      <TableCell>{isPro}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
          <Typography variant="h5" fontWeight={600} mt={5} mb={2}>
            🧠 Deletion Feedback
          </Typography>

          {loadingFeedback ? (
            <CircularProgress />
          ) : feedback.length === 0 ? (
            <Typography>No feedback entries yet.</Typography>
          ) : (
            <Paper elevation={2}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>User ID</b>
                    </TableCell>
                    <TableCell>
                      <b>Reason</b>
                    </TableCell>
                    <TableCell>
                      <b>Comment</b>
                    </TableCell>
                    <TableCell>
                      <b>Deleted At</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedback.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>{f.id}</TableCell>
                      <TableCell>
                        <Chip
                          label={f.reason?.replaceAll("-", " ") || "—"}
                          color={
                            f.reason === "too-expensive"
                              ? "warning"
                              : f.reason === "bugs"
                                ? "error"
                                : "default"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{f.extraText || "—"}</TableCell>
                      <TableCell>
                        {f.deletedAt?.toDate?.().toLocaleString() || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
}
